import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Edit2 } from "iconsax-react";
import { Button, DatePicker, ModalForm, Toast, Prompt } from "@/shared/ui";
import { formatDateToISO, extractErrorMessage, type Locale } from "@/shared/utils";
import type { SelfServiceRequest } from "../types";
import { useUpdateSelfResignationMutation } from "../api";

interface Props {
  request: SelfServiceRequest;
  onClose: () => void;
  onSuccess: () => void;
}

interface ResignationFormValues {
  last_working_day?: Date;
}

function createEditResignationSchema(t: (key: string) => string) {
  return z.object({
    last_working_day: z
      .date({ message: t("requests.termination.validation.lastWorkingDayRequired") })
      .refine(
        function isFuture(date) {
          const tomorrow = new Date();
          tomorrow.setHours(0, 0, 0, 0);
          tomorrow.setDate(tomorrow.getDate() + 1);
          return date >= tomorrow;
        },
        { message: "requests.termination.validation.lastWorkingDayFuture" },
      )
      .optional()
      .refine((d) => d !== undefined, { message: "requests.termination.validation.lastWorkingDayRequired" }),
  });
}

export default function EditSelfResignationForm({ request, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [updateResignation, { isLoading }] = useUpdateSelfResignationMutation();

  const schema = useMemo(function () {
    return createEditResignationSchema(t);
  }, [t]);

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ResignationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      last_working_day: request.details?.last_working_day
        ? new Date(String(request.details.last_working_day) + "T00:00:00")
        : undefined,
    },
  });

  const lastWorkingDay = watch("last_working_day");

  function handleDateChange(v: Date | null) {
    if (v) {
      setValue("last_working_day", v, { shouldValidate: true });
    } else {
      setValue("last_working_day", undefined, { shouldValidate: true });
    }
  }

  async function onSubmit(data: ResignationFormValues) {
    if (isLoading || !data.last_working_day) return;

    try {
      await updateResignation({
        id: request.id,
        data: {
          last_working_day: formatDateToISO(data.last_working_day),
        },
      }).unwrap();

      setPrompt({
        title: t("requests.termination.editSuccessTitle"),
        text: t("requests.termination.editSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("requests.termination.editErrorTitle"),
        text: extractErrorMessage(err, "requests.termination.editErrorText", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSuccess();
    }
  }

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <>
      {prompt && (
        <Prompt
          variant={prompt.variant}
          onClose={handlePromptClose}
          title={prompt.title}
          text={prompt.text}
          namespace="EmployeePortal"
        />
      )}

      <ModalForm
        icon={Edit2}
        onClose={onClose}
        footer={
          <div className="grid grid-cols-[2fr_3fr] gap-3">
            <Button variant="secondary" size="huge" onClick={onClose} type="button">
              {t("leaveForm.cancel")}
            </Button>
            <Button variant="primary" size="huge" type="submit" form="edit-self-resignation-form" disabled={isLoading}>
              {isLoading ? t("leaveForm.saving") : t("leaveForm.save")}
            </Button>
          </div>
        }
      >
      <form id="edit-self-resignation-form" className="flex flex-col h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("requests.termination.editTitle")}</h4>
          <p className="text-body-regular-sm content-base-secondary">{t("requests.termination.editSubtitle")}</p>
        </div>

        <div className="flex-1 overflow-auto flex flex-col gap-4 py-5 px-1">
          {request.application_review_note && (
            <Toast
              color="notice"
              text={`${t("leaveForm.revisionNotePrefix")}: ${request.application_review_note}`}
              closable={false}
              autoClose={false}
              isFullWidth
            />
          )}

          <DatePicker
            label={t("requests.termination.lastWorkingDay")}
            hasRequiredLabel
            placeholder={t("requests.termination.lastWorkingDayPlaceholder")}
            value={lastWorkingDay || null}
            onChange={handleDateChange}
            locale={locale}
            minDate={tomorrow}
            error={errors.last_working_day?.message ? t(errors.last_working_day.message as string) : undefined}
          />
        </div>

      </form>
    </ModalForm>
    </>
  );
}
